import { DiscoveryBar } from '@/components/discovery-bar';
import { MultiStoryViewer, type StoryViewerProfile } from '@/components/story-viewer';
import { FiltersSheet, FiltersSidebar } from '@/components/filters/sidebar';
import { MapModal } from '@/components/map-modal';
import { PageHead } from '@/components/page-head';
import { ProfileCard } from '@/components/profile-card';
import { ProfileModal } from '@/components/profile-modal';
import { QuickFilterStrip } from '@/components/quick-filter-strip';
import { Input } from '@/components/ui/input';
import { FiltersProvider, useFilters } from '@/context/filters-context';
import { type AnalyticsEvent } from '@/hooks/use-analytics';
import { useCardVariant, useFeature } from '@/hooks/use-brand';
import { useProfileOpenMode } from '@/hooks/use-profile-open-mode';
import PageLayout from '@/layouts/page-layout';
import {
  activeFilterCount,
  activeFilterSummary,
  hasActiveFilters,
} from '@/lib/analytics-filters';
import { cn } from '@/lib/utils';
import { Facets, FilterConfig, Gender, SeoMeta } from '@/types';
import { Link, router } from '@inertiajs/react';
import {
  Loader2,
  Plus,
  SearchIcon,
  SearchX,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { prefetchImages } from '@/hooks/use-protected-image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BackendFilters {
  q: string;
  city: string;
  region: string;
  neighborhood_id: string;
  verified: boolean;
  new_profile: boolean;
  available_now: boolean;
  services: string[];
  price_min: number;
  price_max: number;
  attrs: Record<string, string | string[]>;
  sort: string;
}

const SearchInput = () => {
  const { filters, setFilters } = useFilters();
  const { t } = useTranslation('common');

  return (
    <div className="relative mx-4 max-w-xl flex-1">
      <SearchIcon className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400" />
      <Input
        data-testid="search-input"
        type="text"
        placeholder={t('search.placeholder')}
        className="w-full py-2 pr-4 pl-10"
        value={filters.q}
        onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
      />
    </div>
  );
};


// ---------------------------------------------------------------------------
// Spotlight strip — tier-3 profiles in a horizontal scroll row above the grid
// ---------------------------------------------------------------------------

const SpotlightStrip = ({
  profiles,
  openMode,
  gender,
  onOpenModal,
}: {
  profiles: any[];
  openMode: ReturnType<typeof useProfileOpenMode>;
  gender: Gender;
  onOpenModal: (id: string | number) => void;
}) => {
  const { filters } = useFilters();
  const { t } = useTranslation();

  const analyticsEvent: AnalyticsEvent = useMemo(
    () =>
      hasActiveFilters(filters) ? 'profile_click_filtered' : 'profile_click',
    [filters],
  );

  const analyticsMetadata = useMemo(
    () => ({
      gender,
      promotion_tier: 3,
      ...(hasActiveFilters(filters)
        ? { filters: activeFilterSummary(filters) }
        : {}),
    }),
    [filters, gender],
  );

  if (profiles.length === 0) return null;

  return (
    <div className="mb-6">
      <h2 className="mb-3 text-xs font-semibold tracking-widest text-muted-foreground uppercase">
        {t('profile.featured')}
      </h2>
      {/* Horizontally scrollable strip — snaps per card */}
      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {profiles.map((profile, index) => (
          <div key={profile.id} className="w-72 shrink-0 snap-start md:w-80">
            <ProfileCard
              profile={profile}
              variant="magazine"
              index={index}
              openMode={openMode}
              onOpenModal={onOpenModal}
              analyticsEvent={analyticsEvent}
              analyticsMetadata={analyticsMetadata}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hook — returns the actual rendered column count based on viewport width,
// ---------------------------------------------------------------------------
// Responsive column count — mirrors the Tailwind breakpoints used in ProfileGrid.
// ---------------------------------------------------------------------------

function useGridCols(cardVariant: string): number {
  const getColumns = useCallback(() => {
    if (typeof window === 'undefined') return 3;
    const w = window.innerWidth;
    if (cardVariant === 'magazine') return w >= 1024 ? 3 : 2;
    if (w >= 1024) return 4;
    if (w >= 768) return 3;
    return 2;
  }, [cardVariant]);

  const [cols, setCols] = useState(getColumns);

  useEffect(() => {
    const update = () => setCols(getColumns());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [getColumns]);

  return cols;
}

// ---------------------------------------------------------------------------
// Promo card — always last item in the grid, pads the row to always be full
// ---------------------------------------------------------------------------

const PromoCard = () => {
  const { t } = useTranslation();
  return (
    <Link
      href="/profile/create"
      className="group flex aspect-[2/3] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-card/50 p-4 text-center transition-colors hover:border-primary/50 hover:bg-card"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted transition-colors group-hover:bg-primary/10">
        <Plus className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-primary" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{t('promo.your_profile_here')}</p>
        <p className="text-xs text-muted-foreground">{t('promo.add_now')}</p>
      </div>
    </Link>
  );
};

/** Rendered inside FiltersProvider so it can read the live filter state for analytics. */
// ---------------------------------------------------------------------------
// Empty state — shown when no profiles match (no results or no listings yet)
// ---------------------------------------------------------------------------

const GridEmptyState = () => {
  const { t } = useTranslation();
  const { filters, resetFilters } = useFilters();
  const filtersActive = hasActiveFilters(filters);

  return (
    <div className="flex w-full flex-col items-center justify-center gap-5 py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        {filtersActive ? (
          <SearchX className="h-8 w-8 text-muted-foreground" />
        ) : (
          <Users className="h-8 w-8 text-muted-foreground" />
        )}
      </div>

      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold">
          {t(
            filtersActive
              ? 'empty_state.title_filtered'
              : 'empty_state.title_no_filters',
          )}
        </h2>
        <p className="max-w-xs text-sm text-muted-foreground">
          {t(
            filtersActive
              ? 'empty_state.body_filtered'
              : 'empty_state.body_no_filters',
          )}
        </p>
      </div>

      {filtersActive ? (
        <button
          type="button"
          onClick={resetFilters}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:border-primary/50 hover:text-foreground"
        >
          {t('empty_state.cta_filtered')}
        </button>
      ) : (
        <Link
          href="/profile/create"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {t('empty_state.cta_no_filters')}
        </Link>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Profile grid
// ---------------------------------------------------------------------------

const ProfileGrid = ({
  profiles,
  openMode,
  gender,
  onOpenModal,
}: {
  profiles: any[];
  openMode: ReturnType<typeof useProfileOpenMode>;
  gender: Gender;
  onOpenModal: (id: string | number) => void;
}) => {
  const { filters } = useFilters();
  const cardVariant = useCardVariant();

  const analyticsEvent: AnalyticsEvent = useMemo(
    () =>
      hasActiveFilters(filters) ? 'profile_click_filtered' : 'profile_click',
    [filters],
  );

  const analyticsMetadata = useMemo(
    () => ({
      gender,
      ...(hasActiveFilters(filters)
        ? { filters: activeFilterSummary(filters) }
        : {}),
    }),
    [filters, gender],
  );

  const cols = useGridCols(cardVariant);

  if (profiles.length === 0) {
    return <GridEmptyState />;
  }

  const spacersNeeded = (cols - (profiles.length + 1) % cols) % cols;

  return (
    <div className="flex-1">
      <div
        data-testid="profile-grid"
        className={cn(
          'grid grid-cols-2 items-start gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          cardVariant === 'magazine' && 'md:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {profiles.map((profile, index) => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            variant={cardVariant}
            index={index}
            openMode={openMode}
            onOpenModal={onOpenModal}
            analyticsEvent={analyticsEvent}
            analyticsMetadata={analyticsMetadata}
          />
        ))}
        {Array.from({ length: spacersNeeded }).map((_, i) => (
          <div key={`spacer-${i}`} aria-hidden="true" />
        ))}
        <PromoCard />
      </div>
    </div>
  );
};

/** Mobile-only bar shown above the grid with a filter toggle button. */
const MobileFilterBar = ({ onOpen }: { onOpen: () => void }) => {
  const { filters } = useFilters();
  const { t } = useTranslation();
  const count = activeFilterCount(filters);

  return (
    <div className="mb-4 flex items-center gap-2 md:hidden">
      <button
        type="button"
        onClick={onOpen}
        className="relative flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary/50 hover:text-foreground"
      >
        <SlidersHorizontal className="h-4 w-4" />
        {t('filters.title')}
        {count > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
            {count}
          </span>
        )}
      </button>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Stories row — horizontal strip of profiles with active stories
// ---------------------------------------------------------------------------

const StoriesRow = ({ profiles }: { profiles: StoryViewerProfile[] }) => {
  const { t } = useTranslation();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [initialProfileIndex, setInitialProfileIndex] = useState(0);

  const openStories = (index: number) => {
    setInitialProfileIndex(index);
    setViewerOpen(true);
  };

  if (profiles.length === 0) return null;

  return (
    <>
      <MultiStoryViewer
        profiles={profiles}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        initialProfileIndex={initialProfileIndex}
      />

      <div className="mb-4">
        <h2 className="mb-3 text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          {t('stories.section_title')}
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {profiles.map((profile, index) => (
            <button
              key={profile.id}
              type="button"
              className="flex shrink-0 flex-col items-center gap-1.5"
              onClick={() => openStories(index)}
            >
              <div className="rounded-full bg-gradient-to-tr from-primary to-pink-400 p-0.5">
                <div className="h-16 w-16 overflow-hidden rounded-full border-2 border-black bg-card">
                  {profile.avatar?.original_url ? (
                    <img
                      src={profile.avatar.original_url}
                      alt={profile.display_name}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-muted text-sm font-bold text-muted-foreground">
                      {profile.display_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
              <span className="w-16 truncate text-center text-xs text-muted-foreground">
                {profile.display_name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

/** Paginator shape returned by Laravel + Inertia */
interface PaginatedProfiles {
  data: any[];
  current_page: number;
  last_page: number;
  next_page_url: string | null;
  total: number;
  per_page: number;
}

const HomePage = ({
  profiles,
  spotlightProfiles,
  filters: backendFilters,
  filterConfig,
  facets,
  gender,
  seoMeta,
}: {
  profiles: PaginatedProfiles;
  spotlightProfiles: any[];
  filters: BackendFilters;
  filterConfig: FilterConfig;
  facets: Facets;
  gender: Gender;
  seoMeta?: SeoMeta;
}) => {
  const { t } = useTranslation();

  // Pre-warm blob cache for the first page of cards before they mount.
  // Fires all fetches in parallel so cards hit blobCache synchronously on render.
  useMemo(() => {
    const thumbs = profiles.data
      .slice(0, 24)
      .flatMap((p) => {
        const photos = p.listing_photos ?? p.gallery ?? [];
        return photos.slice(0, 1).map((ph: any) => ph?.thumb || ph?.url);
      });
    prefetchImages(thumbs);
  }, [profiles.data]);

  // ---------------------------------------------------------------------------
  // Load-more / infinite-scroll state
  // ---------------------------------------------------------------------------

  const [allProfiles, setAllProfiles] = useState<any[]>(profiles.data);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(
    profiles.next_page_url,
  );
  const [loadingMore, setLoadingMore] = useState(false);

  // When Inertia delivers a fresh page response (only: ['profiles']):
  //   page 1  → filter/search changed → reset the list
  //   page N  → "load more" → append to the list
  useEffect(() => {
    if (profiles.current_page === 1) {
      setAllProfiles(profiles.data);
    } else {
      setAllProfiles((prev) => [...prev, ...profiles.data]);
    }
    setNextPageUrl(profiles.next_page_url);
    setLoadingMore(false);
  }, [profiles]);

  const loadMore = useCallback(() => {
    if (!nextPageUrl || loadingMore) return;
    setLoadingMore(true);
    router.get(
      nextPageUrl,
      { per_page: 24 },
      {
        preserveState: true,
        preserveScroll: true,
        only: ['profiles'],
      },
    );
  }, [nextPageUrl, loadingMore]);

  // ---------------------------------------------------------------------------
  // Re-hydrate attrs: the backend returns them as flat strings/arrays;
  // number-type attributes are stored as [min, max] tuples on the frontend.
  // ---------------------------------------------------------------------------

  const numberKeys = new Set(
    filterConfig.attributes
      .filter((a) => a.type === 'number')
      .map((a) => a.key),
  );

  const attrs: Record<string, string[] | [number, number] | boolean> = {};
  for (const [key, value] of Object.entries(backendFilters.attrs ?? {})) {
    if (numberKeys.has(key) && Array.isArray(value) && value.length === 2) {
      attrs[key] = [Number(value[0]), Number(value[1])];
    } else if (value === '1' || value === 'true') {
      attrs[key] = true;
    } else if (Array.isArray(value)) {
      attrs[key] = value as string[];
    }
  }

  const initialFilters = {
    q: backendFilters.q,
    sort: backendFilters.sort ?? '',
    location: {
      city: backendFilters.city,
      region: backendFilters.region,
      neighborhoodId: backendFilters.neighborhood_id ?? '',
    },
    services: backendFilters.services,
    priceRange: [backendFilters.price_min, backendFilters.price_max] as [
      number,
      number,
    ],
    availability: {
      onlineNow: false,
      verified: backendFilters.verified,
      newProfile: backendFilters.new_profile,
    },
    attrs,
  };

  const advancedFilters = useFeature('advanced_filters');
  const openMode = useProfileOpenMode();
  const [modalProfileId, setModalProfileId] = useState<string | number | null>(
    null,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  return (
    <PageLayout>
      <PageHead seoMeta={seoMeta} />
      <FiltersProvider
        initialFilters={initialFilters}
        filterConfig={filterConfig}
        facets={facets}
      >
        <ProfileModal
          profileId={modalProfileId}
          onClose={() => setModalProfileId(null)}
        />

        <MapModal
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          gender={gender ?? undefined}
        />

        <DiscoveryBar gender={gender} onMapOpen={() => setMapOpen(true)} />
        <QuickFilterStrip filters={backendFilters} />

        <section className="py-6">
          <div className="container mx-auto px-4">
            {/* Mobile filter toggle */}
            {advancedFilters && (
              <div className="mb-4 md:hidden">
                <MobileFilterBar onOpen={() => setFiltersOpen(true)} />
              </div>
            )}

            <div className="flex gap-6">
              {/* Desktop sidebar — hidden below md */}
              {advancedFilters && <FiltersSidebar />}

              {/* Mobile sheet */}
              {advancedFilters && (
                <FiltersSheet
                  open={filtersOpen}
                  onClose={() => setFiltersOpen(false)}
                />
              )}

              <div className="flex min-w-0 flex-1 flex-col gap-6">
                <StoriesRow
                  profiles={allProfiles.filter(
                    (p): p is StoryViewerProfile => Array.isArray(p.stories) && p.stories.length > 0,
                  )}
                />
                <SpotlightStrip
                  profiles={spotlightProfiles}
                  openMode={openMode}
                  gender={gender}
                  onOpenModal={setModalProfileId}
                />
                <ProfileGrid
                  profiles={allProfiles}
                  openMode={openMode}
                  gender={gender}
                  onOpenModal={setModalProfileId}
                />

                {nextPageUrl && (
                  <div className="flex justify-center py-4">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="flex items-center gap-2 rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loadingMore && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {loadingMore
                        ? t('actions.loading')
                        : t('actions.load_more')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </FiltersProvider>
    </PageLayout>
  );
};

export default HomePage;
